import { useEffect, useState, useCallback } from 'react';
import { useAuth } from 'zite-auth-sdk';
import { getOnboardingData, saveOnboardingStep, saveSignature, GetOnboardingDataOutputType } from 'zite-endpoints-sdk';
import { uploadFile } from 'zite-file-upload-sdk';
import { toast } from 'sonner';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Lock, CheckCircle } from 'lucide-react';
import WizardProgress from '@/components/onboarding/WizardProgress';
import StepNavigation from '@/components/onboarding/StepNavigation';
import FieldRow from '@/components/onboarding/FieldRow';
import Step6Documents from '@/components/onboarding/Step6Documents';
import Step7Policies from '@/components/onboarding/Step7Policies';

type StepData = GetOnboardingDataOutputType['steps'][0];

const EMPLOYMENT_TYPES = [
  { value: 'Full Time', label: 'Full Time' }, { value: 'Part Time', label: 'Part Time' },
  { value: 'Temporary', label: 'Temporary' }, { value: 'Casual', label: 'Casual' },
];

export default function Onboarding() {
  const { user } = useAuth();
  const [steps, setSteps] = useState<StepData[]>([]);
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [isLocked, setIsLocked] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [userProfile, setUserProfile] = useState<GetOnboardingDataOutputType['userProfile'] | null>(null);

  const loadData = useCallback(async () => {
    if (!user) return;
    try {
      const d = await getOnboardingData({});
      setSteps(d.steps);
      setIsLocked(d.isLocked);
      setIsSubmitted(d.isSubmitted);
      setUserProfile(d.userProfile);
      // Find first incomplete step
      const firstIncomplete = d.steps.find(s => s.status !== 'Complete');
      const startStep = firstIncomplete ? firstIncomplete.stepNumber : 7;
      setCurrentStep(startStep);
      setFormData((d.steps[startStep - 1]?.formData as Record<string, string>) || {});
    } finally { setLoading(false); }
  }, [user]);

  useEffect(() => { loadData(); }, [loadData]);

  // Pre-fill from user profile when changing steps
  useEffect(() => {
    if (!steps.length) return;
    const step = steps[currentStep - 1];
    const existing = step?.formData as Record<string, string> || {};
    const prefill: Record<string, string> = { ...existing };
    if (currentStep === 1 && userProfile) {
      if (!prefill.firstName && userProfile.firstName) prefill.firstName = userProfile.firstName;
      if (!prefill.lastName && userProfile.lastName) prefill.lastName = userProfile.lastName;
      if (!prefill.email && userProfile.email) prefill.email = userProfile.email;
      if (!prefill.preferredName && userProfile.preferredName) prefill.preferredName = userProfile.preferredName;
      if (!prefill.dateOfBirth && userProfile.dateOfBirth) prefill.dateOfBirth = userProfile.dateOfBirth;
      if (!prefill.niNumber && userProfile.niNumber) prefill.niNumber = userProfile.niNumber;
    }
    if (currentStep === 2 && userProfile) {
      ['addressLine1', 'addressLine2', 'town', 'county', 'postcode'].forEach(k => {
        if (!prefill[k] && (userProfile as any)[k]) prefill[k] = (userProfile as any)[k];
      });
    }
    if (currentStep === 4 && userProfile) {
      if (!prefill.position && userProfile.position) prefill.position = userProfile.position;
      if (!prefill.department && userProfile.department) prefill.department = userProfile.department;
      if (!prefill.startDate && userProfile.startDate) prefill.startDate = userProfile.startDate;
      if (!prefill.employmentType && userProfile.employmentType) prefill.employmentType = userProfile.employmentType;
      if (!prefill.tipJarReference && userProfile.tipJarReference) prefill.tipJarReference = userProfile.tipJarReference;
    }
    setFormData(prefill);
  }, [currentStep, steps]);

  const f = (key: string) => formData[key] || '';
  const set = (key: string) => (val: string) => setFormData(d => ({ ...d, [key]: val }));

  const validateStep = () => {
    if (currentStep === 1 && (!f('firstName') || !f('lastName') || !f('dateOfBirth') || !f('niNumber'))) {
      toast.error('Please fill in all required fields'); return false;
    }
    if (currentStep === 2 && (!f('addressLine1') || !f('town') || !f('postcode'))) {
      toast.error('Please fill in address, town and postcode'); return false;
    }
    if (currentStep === 3 && (!f('emergencyContactName') || !f('emergencyContactPhone'))) {
      toast.error('Emergency contact name and phone are required'); return false;
    }
    if (currentStep === 5 && (!f('bankAccountHolder') || !f('bankName') || !f('sortCode') || !f('accountNumber'))) {
      toast.error('Please fill in all bank details'); return false;
    }
    return true;
  };

  const saveStep = async (status: 'Draft' | 'Complete', isFinal = false) => {
    setSaving(true);
    try {
      await saveOnboardingStep({ stepNumber: currentStep, formData, status, finalSubmit: isFinal });
      setSteps(prev => prev.map(s => s.stepNumber === currentStep ? { ...s, status, formData } : s));
      if (isFinal) { setIsSubmitted(true); setIsLocked(true); toast.success('Onboarding submitted! 🎉'); }
    } catch (e: any) { toast.error(e.message || 'Failed to save'); throw e; }
    finally { setSaving(false); }
  };

  const handleNext = async () => {
    if (!validateStep()) return;
    const isFinal = currentStep === 7;
    await saveStep('Complete', isFinal);
    if (!isFinal) { setCurrentStep(s => s + 1); }
  };

  const handleBack = () => {
    setCurrentStep(s => Math.max(1, s - 1));
  };

  const handleSaveDraft = async () => {
    await saveStep('Draft').catch(() => {});
    toast.success('Draft saved');
  };

  const goToStep = (step: number) => {
    setCurrentStep(step);
  };

  if (loading) return <div className="px-4 md:px-8 py-6 max-w-3xl mx-auto space-y-4"><Skeleton className="h-20" /><Skeleton className="h-64" /></div>;

  if (isSubmitted && isLocked) {
    return (
      <div className="px-4 md:px-8 py-12 max-w-lg mx-auto text-center">
        <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-8 h-8 text-green-500" />
        </div>
        <h1 className="text-xl font-bold text-foreground mb-2">Onboarding Complete!</h1>
        <p className="text-muted-foreground mb-6">Your form has been submitted and sent to HR for review. You'll be notified once it's been processed.</p>
        <div className="bg-card border border-border rounded-lg p-4 text-left space-y-2 mb-6">
          {steps.map(s => (
            <div key={s.stepNumber} className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">{s.stepName}</span>
              <span className={s.status === 'Complete' ? 'text-green-500 font-medium' : 'text-muted-foreground'}>{s.status === 'Complete' ? '✓ Complete' : s.status}</span>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
          <Lock className="w-3.5 h-3.5" /> Form locked — contact HR to request changes
        </div>
      </div>
    );
  }

  const completedSteps = steps.filter(s => s.status === 'Complete').map(s => s.stepNumber);

  return (
    <div className="px-4 md:px-8 py-4 md:py-6 max-w-3xl mx-auto pb-20 md:pb-6">
      <div className="mb-6">
        <h1 className="text-xl font-bold text-foreground">Onboarding</h1>
        <p className="text-sm text-muted-foreground mt-1">Complete all steps to finish your employee setup. You can save and return anytime.</p>
      </div>

      <WizardProgress currentStep={currentStep} completedSteps={completedSteps} onStepClick={goToStep} />

      <div className="bg-card border border-border rounded-lg p-6">
        <h2 className="text-base font-semibold text-foreground mb-1">Step {currentStep}: {steps[currentStep - 1]?.stepName}</h2>
        <p className="text-sm text-muted-foreground mb-5">
          {currentStep === 1 && 'Your personal information as it will appear on employment records.'}
          {currentStep === 2 && 'Your current home address for HR records.'}
          {currentStep === 3 && 'Who should we contact in an emergency?'}
          {currentStep === 4 && 'Your employment information at The Rattlebone.'}
          {currentStep === 5 && 'Your bank account details for salary payments.'}
          {currentStep === 6 && 'Upload your right to work and identification documents.'}
          {currentStep === 7 && 'Please read and acknowledge each policy before submitting.'}
        </p>

        {currentStep === 1 && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FieldRow label="First Name" id="firstName" value={f('firstName')} onChange={set('firstName')} required placeholder="Your first name" />
              <FieldRow label="Last Name" id="lastName" value={f('lastName')} onChange={set('lastName')} required placeholder="Your last name" />
            </div>
            <FieldRow label="Preferred Name (if different)" id="preferredName" value={f('preferredName')} onChange={set('preferredName')} placeholder="What would you like to be called?" />
            <div className="grid grid-cols-2 gap-4">
              <FieldRow label="Date of Birth" id="dateOfBirth" value={f('dateOfBirth')} onChange={set('dateOfBirth')} type="date" required />
              <FieldRow label="National Insurance Number" id="niNumber" value={f('niNumber')} onChange={set('niNumber')} required placeholder="AB 12 34 56 C" />
            </div>
            <FieldRow label="Phone Number" id="phone" value={f('phone')} onChange={set('phone')} placeholder="07xxx xxxxxx" type="tel" />
            <FieldRow label="Email Address" id="email" value={f('email')} onChange={set('email')} readOnly />
          </div>
        )}

        {currentStep === 2 && (
          <div className="space-y-4">
            <FieldRow label="Address Line 1" id="addressLine1" value={f('addressLine1')} onChange={set('addressLine1')} required placeholder="House number and street name" />
            <FieldRow label="Address Line 2" id="addressLine2" value={f('addressLine2')} onChange={set('addressLine2')} placeholder="Flat, apartment, etc. (optional)" />
            <div className="grid grid-cols-2 gap-4">
              <FieldRow label="Town / City" id="town" value={f('town')} onChange={set('town')} required placeholder="Town" />
              <FieldRow label="County" id="county" value={f('county')} onChange={set('county')} placeholder="County" />
            </div>
            <FieldRow label="Postcode" id="postcode" value={f('postcode')} onChange={set('postcode')} required placeholder="e.g. SN16 0LD" />
          </div>
        )}

        {currentStep === 3 && (
          <div className="space-y-4">
            <FieldRow label="Contact Name" id="emergencyContactName" value={f('emergencyContactName')} onChange={set('emergencyContactName')} required placeholder="Full name" />
            <FieldRow label="Relationship to You" id="emergencyContactRelationship" value={f('emergencyContactRelationship')} onChange={set('emergencyContactRelationship')} placeholder="e.g. Parent, Partner, Sibling" />
            <div className="grid grid-cols-2 gap-4">
              <FieldRow label="Contact Phone Number" id="emergencyContactPhone" value={f('emergencyContactPhone')} onChange={set('emergencyContactPhone')} required placeholder="07xxx xxxxxx" type="tel" />
              <FieldRow label="Alternative Phone (optional)" id="emergencyAltPhone" value={f('emergencyAltPhone')} onChange={set('emergencyAltPhone')} placeholder="Alternative number" type="tel" />
            </div>
          </div>
        )}

        {currentStep === 4 && (() => {
          const empType = f('employmentType') || userProfile?.employmentType || '';
          const isTipJarEligible = empType === 'Full Time' || empType === 'Part Time';
          return (
            <div className="space-y-4">
              {/* Pre-filled notice if admin has set any fields */}
              {(userProfile?.position || userProfile?.department || userProfile?.startDate) && (
                <div className="flex items-start gap-2 bg-primary/5 border border-primary/20 rounded-lg px-4 py-3 text-xs text-foreground/80">
                  <span className="mt-0.5">ℹ️</span>
                  <span>Some fields below have been pre-filled by management. They are shown as read-only — contact HR if any details look incorrect.</span>
                </div>
              )}

              <FieldRow
                label="Job Title / Position"
                id="position"
                value={f('position')}
                onChange={set('position')}
                placeholder="e.g. Bar Staff, Kitchen Porter, CDP"
                readOnly={!!userProfile?.position}
                hint={userProfile?.position ? 'Pre-filled by management' : undefined}
              />

              <FieldRow
                label="Department"
                id="department"
                value={f('department')}
                onChange={set('department')}
                options={[
                  { value: 'Front of House', label: 'Front of House' },
                  { value: 'Back of House', label: 'Back of House' },
                  { value: 'Kitchen', label: 'Kitchen' },
                  { value: 'Management', label: 'Management' },
                ]}
                readOnly={!!userProfile?.department}
                hint={userProfile?.department ? 'Pre-filled by management' : undefined}
              />

              <div className="grid grid-cols-2 gap-4">
                <FieldRow
                  label="Start Date"
                  id="startDate"
                  value={f('startDate')}
                  onChange={set('startDate')}
                  type="date"
                  readOnly={!!userProfile?.startDate}
                  hint={userProfile?.startDate ? 'Pre-filled by management' : undefined}
                />
                <FieldRow
                  label="Employment Type"
                  id="employmentType"
                  value={empType}
                  onChange={set('employmentType')}
                  options={EMPLOYMENT_TYPES}
                  readOnly={!!userProfile?.employmentType}
                  hint={userProfile?.employmentType ? 'Pre-filled by management' : undefined}
                />
              </div>

              {/* TipJar enrolment notice */}
              {isTipJarEligible && (
                <div className="border border-primary/30 bg-primary/5 rounded-lg p-4 space-y-3">
                  <div>
                    <p className="text-sm font-semibold text-foreground mb-1">💳 TipJar Enrolment</p>
                    <p className="text-xs text-muted-foreground">
                      As a <strong className="text-foreground">{empType}</strong> employee, you will automatically be enrolled in <strong className="text-foreground">TipJar</strong> — our cashless card tipping platform. This means customers can tip you directly to your bank account when paying by card.
                    </p>
                    <p className="text-xs text-muted-foreground mt-1.5">
                      If you already have a TipJar account, enter your username below so we can link it to your profile. If you don't have one yet, leave it blank — we'll set it up for you during onboarding.
                    </p>
                  </div>
                  <FieldRow
                    label="TipJar Username (optional)"
                    id="tipJarReference"
                    value={f('tipJarReference')}
                    onChange={set('tipJarReference')}
                    placeholder="Your TipJar username or account email"
                    hint="You can find this in your TipJar app under Account Settings"
                  />
                </div>
              )}

              {/* Casual/Temporary notice */}
              {(empType === 'Casual' || empType === 'Temporary') && (
                <div className="border border-border bg-muted/30 rounded-lg px-4 py-3 text-xs text-muted-foreground">
                  ℹ️ Casual and Temporary staff are not automatically enrolled in TipJar. Speak to your manager if you'd like to be included.
                </div>
              )}
            </div>
          );
        })()}

        {currentStep === 5 && (() => {
          // Read employment type from step 4's saved data or user profile
          const step4Data = steps[3]?.formData as Record<string, string> || {};
          const empType = step4Data.employmentType || userProfile?.employmentType || '';
          const isTipJarEligible = empType === 'Full Time' || empType === 'Part Time';
          const isTemporaryOrCasual = empType === 'Temporary' || empType === 'Casual';

          return (
            <div className="space-y-4">
              {/* Employment-type-aware payroll/TipJar notice */}
              {isTipJarEligible && (
                <div className="border border-primary/30 bg-primary/5 rounded-lg px-4 py-3 text-xs">
                  <p className="font-semibold text-foreground mb-1">💳 Payroll & TipJar Enrolment</p>
                  <p className="text-muted-foreground">
                    As a <strong className="text-foreground">{empType}</strong> employee, you will be enrolled in our payroll system (Xero) and automatically added to <strong className="text-foreground">TipJar</strong> — our cashless card tipping platform. Once you've accepted your TipJar invitation, customers can tip you directly to your bank account when paying by card.
                  </p>
                </div>
              )}

              {isTemporaryOrCasual && (
                <div className="border border-amber-500/30 bg-amber-500/5 rounded-lg px-4 py-3 text-xs">
                  <p className="font-semibold text-foreground mb-1">📋 Payroll Enrolment — Temporary & Casual Staff</p>
                  <p className="text-muted-foreground">
                    Please <strong className="text-foreground">inquire with your manager</strong> if you wish to be enrolled onto the payroll — this may be a possibility depending on your contract. You will be advised whether PAYE processing or a self-employed arrangement applies to you.
                  </p>
                </div>
              )}

              {!empType && (
                <div className="border border-border bg-muted/30 rounded-lg px-4 py-3 text-xs text-muted-foreground">
                  ℹ️ Complete Step 4 (Employment Details) to see your payroll enrolment information.
                </div>
              )}

              <FieldRow label="Name of Account Holder" id="bankAccountHolder" value={f('bankAccountHolder')} onChange={set('bankAccountHolder')} required placeholder="Full name as on bank account" />
              <FieldRow label="Bank / Building Society Name" id="bankName" value={f('bankName')} onChange={set('bankName')} required placeholder="e.g. Lloyds Bank, NatWest, Monzo" />
              <div className="grid grid-cols-2 gap-4">
                <FieldRow label="Sort Code" id="sortCode" value={f('sortCode')} onChange={set('sortCode')} required placeholder="00-00-00" />
                <FieldRow label="Account Number" id="accountNumber" value={f('accountNumber')} onChange={set('accountNumber')} required placeholder="8-digit account number" />
              </div>
              <div className="bg-muted/40 border border-border rounded-lg px-4 py-3 text-xs text-muted-foreground">
                🔒 Bank details are stored securely and only visible to payroll admin.
              </div>
            </div>
          );
        })()}

        {currentStep === 6 && <Step6Documents formData={formData} onUpdate={(k, v) => setFormData(d => ({ ...d, [k]: v }))} />}
        {currentStep === 7 && <Step7Policies formData={formData} onUpdate={(k, v) => setFormData(d => ({ ...d, [k]: v }))} />}

        <StepNavigation
          currentStep={currentStep}
          totalSteps={7}
          saving={saving}
          onBack={handleBack}
          onSaveDraft={handleSaveDraft}
          onNext={handleNext}
          isLastStep={currentStep === 7}
        />
      </div>
    </div>
  );
}
